import { hrDebugTrace, isHrDebugPricing } from "./hrDebugFlags";

/** Retained for call-site compatibility; ingest logging removed. */
export function emitPublicPriceSourceTrace(_payload) {}

/** Retained for call-site compatibility; ingest logging removed. */
export function emitPublicCommissionBugTrace(_payload) {}

/** מפתחות רלוונטיים לבדיקת עדיפות / fallback — ללא PII */
export function summarizePublicSiteSettingsForDebug(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    public_hotel_commission_type: raw.public_hotel_commission_type ?? null,
    public_hotel_commission_value: raw.public_hotel_commission_value ?? null,
    public_vacation_package_commission_type: raw.public_vacation_package_commission_type ?? null,
    public_vacation_package_commission_value: raw.public_vacation_package_commission_value ?? null,
    custom_site_commission_percent: raw.custom_site_commission_percent ?? null,
    custom_commission_percentage: raw.custom_commission_percentage ?? null,
    public_site_fixed_hotel_commission: raw.public_site_fixed_hotel_commission ?? null,
    public_site_fixed_vacation_package_commission: raw.public_site_fixed_vacation_package_commission ?? null,
    hotel_node: raw.hotel ?? null,
    vacation_package_node: raw.vacation_package ?? null,
    flight_node: raw.flight ?? null,
  };
}

const ALLOWED_TYPES = new Set(["fixed", "percent"]);
export const DEFAULT_PUBLIC_SITE_COMMISSION_PERCENT = 0;

function toFinite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function normalizeType(type) {
  const t = String(type || "fixed").trim().toLowerCase();
  return ALLOWED_TYPES.has(t) ? t : "fixed";
}

function normalizeNode(node, fallbackValue = 0) {
  if (node && typeof node === "object" && !Array.isArray(node)) {
    const type = normalizeType(node.type);
    const value = Math.max(0, toFinite(node.value));
    return { type, value };
  }
  return { type: "fixed", value: Math.max(0, toFinite(fallbackValue)) };
}

function nodeFrom(type, value) {
  return {
    type: normalizeType(type),
    value: Math.max(0, toFinite(value)),
  };
}

/**
 * צומת מ־payment_settings.public_pricing_settings (hotel / flight / vacation_package).
 * עדיפות על פני שדות שטוחים legacy — כולל value: 0 (בלי ליפול ל־markup ברירת מחדל).
 */
function tryExplicitNestedCategoryNode(src, categoryKey) {
  const nested = src[categoryKey];
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) return null;
  const hasType = Object.prototype.hasOwnProperty.call(nested, "type");
  const hasValue = Object.prototype.hasOwnProperty.call(nested, "value");
  if (!hasType && !hasValue) return null;
  return normalizeNode(nested, 0);
}

function debugPublicCommission(payload) {
  if (!isHrDebugPricing()) return;
  hrDebugTrace("[PUBLIC_PRICE_TRACE] applyPublicCommission invocation", payload);
}

function resolveHotelCommissionNode(src) {
  const nestedHotel = tryExplicitNestedCategoryNode(src, "hotel");
  if (nestedHotel != null) return nestedHotel;
  if (
    hasValue(src.public_hotel_commission_type) ||
    hasValue(src.public_hotel_commission_value)
  ) {
    return nodeFrom(src.public_hotel_commission_type || "fixed", src.public_hotel_commission_value);
  }
  if (
    hasValue(src.public_site_fixed_hotel_commission) &&
    toFinite(src.public_site_fixed_hotel_commission) > 0
  ) {
    return nodeFrom("fixed", src.public_site_fixed_hotel_commission);
  }
  if (
    hasValue(src.custom_site_commission_percent) &&
    toFinite(src.custom_site_commission_percent) > 0
  ) {
    return nodeFrom("percent", src.custom_site_commission_percent);
  }
  if (
    hasValue(src.custom_commission_percentage) &&
    toFinite(src.custom_commission_percentage) > 0
  ) {
    return nodeFrom("percent", src.custom_commission_percentage);
  }
  return resolveDefaultPercentCommissionNode(src);
}

function resolveDefaultPercentCommissionNode(src) {
  if (
    hasValue(src.custom_site_commission_percent) &&
    toFinite(src.custom_site_commission_percent) > 0
  ) {
    return nodeFrom("percent", src.custom_site_commission_percent);
  }
  if (
    hasValue(src.custom_commission_percentage) &&
    toFinite(src.custom_commission_percentage) > 0
  ) {
    return nodeFrom("percent", src.custom_commission_percentage);
  }
  return nodeFrom("fixed", 0);
}

function resolveVacationPackageCommissionNode(src) {
  const nestedVp = tryExplicitNestedCategoryNode(src, "vacation_package");
  if (nestedVp != null) return nestedVp;
  if (
    hasValue(src.public_vacation_package_commission_type) ||
    hasValue(src.public_vacation_package_commission_value)
  ) {
    return nodeFrom(
      src.public_vacation_package_commission_type || "fixed",
      src.public_vacation_package_commission_value
    );
  }
  if (
    hasValue(src.public_site_fixed_vacation_package_commission) &&
    toFinite(src.public_site_fixed_vacation_package_commission) > 0
  ) {
    return nodeFrom("fixed", src.public_site_fixed_vacation_package_commission);
  }
  return resolveDefaultPercentCommissionNode(src);
}

function resolveFlightCommissionNode(src) {
  const nestedFlight = tryExplicitNestedCategoryNode(src, "flight");
  if (nestedFlight != null) return nestedFlight;
  if (
    hasValue(src.public_flight_commission_type) ||
    hasValue(src.public_flight_commission_value)
  ) {
    return nodeFrom(src.public_flight_commission_type || "fixed", src.public_flight_commission_value);
  }
  if (
    hasValue(src.public_site_fixed_flight_commission) &&
    toFinite(src.public_site_fixed_flight_commission) > 0
  ) {
    return nodeFrom("fixed", src.public_site_fixed_flight_commission);
  }
  return resolveDefaultPercentCommissionNode(src);
}

export function normalizePublicSitePricingSettings(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    hotel: resolveHotelCommissionNode(src),
    flight: resolveFlightCommissionNode(src),
    vacation_package: resolveVacationPackageCommissionNode(src),
  };
}

export function applyPublicCommission(amount, commission, bugTraceMeta = null) {
  const base = toFinite(amount);
  if (base <= 0) return base;
  const cfg = normalizeNode(commission, 0);
  debugPublicCommission({
    basePrice: base,
    commissionType: cfg.type,
    commissionValue: cfg.value,
    fallbackUsed: cfg.value <= 0,
    finalPrice:
      cfg.value <= 0 ? base : cfg.type === "percent" ? base * (1 + cfg.value / 100) : base + cfg.value,
  });
  let result = base;
  if (cfg.value <= 0) {
    result = base;
  } else if (cfg.type === "percent") {
    result = base * (1 + cfg.value / 100);
  } else {
    result = base + cfg.value;
  }
  return result;
}

export function resolveCategoryCommission(settings, categoryKey) {
  const normalized = normalizePublicSitePricingSettings(settings);
  const node = normalized[categoryKey] || { type: "fixed", value: 0 };
  return node;
}

/**
 * Apply the agent's public-site hotel commission on top of an already-resolved
 * agent hotel search price VM (HR markup already included).
 */
export function applyPublicHotelCommissionToVm(vm, publicSitePricingSettings) {
  if (!vm || vm.finalPrice == null || publicSitePricingSettings == null) return vm;
  const hotelCommission = resolveCategoryCommission(publicSitePricingSettings, "hotel");
  return {
    ...vm,
    finalPrice: applyPublicCommission(vm.finalPrice, hotelCommission),
    publicHotelCommission: hotelCommission,
  };
}

